import React, { useState, useEffect, useCallback } from 'react';
import TherapistClientList from './TherapistClientList';
import ClientBoundaryOverview from './ClientBoundaryOverview';
import RiskIndicator from './RiskIndicator';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

/**
 * BoundaryMonitoringDashboard - Main container for boundary monitoring
 * Provides role-based views for Therapist, Clinical Supervisor, and Admin
 */
const BoundaryMonitoringDashboard = ({ token, userRole }) => {
  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [clients, setClients] = useState([]);
  const [clientsSummary, setClientsSummary] = useState(null);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [clientOverview, setClientOverview] = useState(null);
  const [clientInfo, setClientInfo] = useState(null);
  const [includeAllClients, setIncludeAllClients] = useState(false);

  // Determine user role for view selection
  const roles = userRole || [];
  const isAdmin = roles.includes('admin');
  const isSupervisor = roles.includes('clinical_supervisor');
  const isTherapist = roles.includes('therapist');

  // Fetch clients list
  const fetchClients = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(
        `${API_BASE}/boundary-monitoring/therapist/clients?includeAll=${includeAllClients}`,
        {
          headers: {
            'x-auth-token': token,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch clients');
      }

      const data = await response.json();
      setClients(data.clients || []);
      setClientsSummary(data.summary || null);
    } catch (err) {
      console.error('[BoundaryMonitoring] Error fetching clients:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, includeAllClients]);

  // Fetch client overview
  const fetchClientOverview = useCallback(async (clientId) => {
    if (!clientId) return;

    try {
      setLoading(true);
      setError(null);

      const response = await fetch(
        `${API_BASE}/boundary-monitoring/client/${clientId}/overview?days=30`,
        {
          headers: {
            'x-auth-token': token,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch client overview');
      }

      const data = await response.json();
      setClientOverview(data.overview || null);
      setClientInfo(data.client || null);
    } catch (err) {
      console.error('[BoundaryMonitoring] Error fetching client overview:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Handle flag acknowledgment
  const handleAcknowledge = async (flagId, note) => {
    try {
      const response = await fetch(
        `${API_BASE}/boundary-monitoring/flags/${flagId}/acknowledge`,
        {
          method: 'PUT',
          headers: {
            'x-auth-token': token,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ note })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to acknowledge flag');
      }

      // Refresh client overview
      if (selectedClientId) {
        fetchClientOverview(selectedClientId);
      }
    } catch (err) {
      console.error('[BoundaryMonitoring] Error acknowledging flag:', err);
      setError(err.message);
    }
  };

  // Handle flag resolution
  const handleResolve = async (flagId, note) => {
    try {
      const response = await fetch(
        `${API_BASE}/boundary-monitoring/flags/${flagId}/resolve`,
        {
          method: 'PUT',
          headers: {
            'x-auth-token': token,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ resolutionType: 'clinical_decision', note })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to resolve flag');
      }

      // Refresh both client overview and client list
      if (selectedClientId) {
        fetchClientOverview(selectedClientId);
      }
      fetchClients();
    } catch (err) {
      console.error('[BoundaryMonitoring] Error resolving flag:', err);
      setError(err.message);
    }
  };

  // Handle flag dismissal
  const handleDismiss = async (flagId, note) => {
    try {
      const response = await fetch(
        `${API_BASE}/boundary-monitoring/flags/${flagId}/dismiss`,
        {
          method: 'PUT',
          headers: {
            'x-auth-token': token,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ note })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to dismiss flag');
      }

      // Refresh both client overview and client list
      if (selectedClientId) {
        fetchClientOverview(selectedClientId);
      }
      fetchClients();
    } catch (err) {
      console.error('[BoundaryMonitoring] Error dismissing flag:', err);
      setError(err.message);
    }
  };

  // Handle client selection
  const handleSelectClient = (clientId) => {
    setSelectedClientId(clientId);
    fetchClientOverview(clientId);
  };

  // Handle back to list
  const handleBackToList = () => {
    setSelectedClientId(null);
    setClientOverview(null);
    setClientInfo(null);
  };

  // Initial load
  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  // Styles
  const containerStyle = {
    padding: '20px',
    maxWidth: '1400px',
    margin: '0 auto'
  };

  const headerStyle = {
    marginBottom: '20px'
  };

  const titleStyle = {
    fontSize: '24px',
    fontWeight: '600',
    color: '#333',
    marginBottom: '8px'
  };

  const subtitleStyle = {
    fontSize: '14px',
    color: '#666'
  };

  const errorStyle = {
    padding: '12px 16px',
    backgroundColor: '#f8d7da',
    color: '#721c24',
    borderRadius: '4px',
    marginBottom: '16px'
  };

  const controlsStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
    padding: '12px',
    backgroundColor: '#f8f9fa',
    borderRadius: '6px'
  };

  const checkboxLabelStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    cursor: 'pointer'
  };

  const refreshButtonStyle = {
    padding: '8px 16px',
    backgroundColor: '#007bff',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px'
  };

  const layoutStyle = {
    display: 'grid',
    gridTemplateColumns: selectedClientId ? '1fr' : '350px 1fr',
    gap: '20px'
  };

  return (
    <div style={containerStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <h2 style={titleStyle}>Boundary Monitoring Dashboard</h2>
        <p style={subtitleStyle}>
          Monitor therapeutic drift (CBC) and parasocial detection patterns across your clients.
          {isAdmin && ' (Admin View: All clients)'}
          {isSupervisor && !isAdmin && ' (Supervisor View: Service group clients)'}
          {isTherapist && !isSupervisor && !isAdmin && ' (Therapist View: Your assigned clients)'}
        </p>
      </div>

      {/* Error Display */}
      {error && (
        <div style={errorStyle}>
          Error: {error}
          <button
            onClick={() => setError(null)}
            style={{ marginLeft: '10px', cursor: 'pointer', background: 'none', border: 'none', fontWeight: 'bold' }}
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Controls */}
      {!selectedClientId && (
        <div style={controlsStyle}>
          <label style={checkboxLabelStyle}>
            <input
              type="checkbox"
              checked={includeAllClients}
              onChange={(e) => setIncludeAllClients(e.target.checked)}
            />
            Show all clients (including those without concerns)
          </label>
          <button
            style={refreshButtonStyle}
            onClick={fetchClients}
            disabled={loading}
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      )}

      {/* Main Content */}
      {selectedClientId ? (
        // Client Detail View
        <ClientBoundaryOverview
          client={clientInfo}
          overview={clientOverview}
          onAcknowledge={handleAcknowledge}
          onResolve={handleResolve}
          onDismiss={handleDismiss}
          onBack={handleBackToList}
          loading={loading}
        />
      ) : (
        // Client List View
        <div style={layoutStyle}>
          <TherapistClientList
            clients={clients}
            summary={clientsSummary}
            onSelectClient={handleSelectClient}
            selectedClientId={selectedClientId}
            loading={loading}
          />

          {/* Quick Stats Panel */}
          <div style={{
            backgroundColor: '#fff',
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            padding: '20px'
          }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px' }}>
              Quick Overview
            </h3>

            {clientsSummary ? (
              <div>
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>
                    Risk Distribution
                  </div>
                  <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <RiskIndicator level="critical" size="small" />
                      <span>{clientsSummary.atRisk?.critical || 0}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <RiskIndicator level="high" size="small" />
                      <span>{clientsSummary.atRisk?.high || 0}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <RiskIndicator level="medium" size="small" />
                      <span>{clientsSummary.atRisk?.medium || 0}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <RiskIndicator level="low" size="small" />
                      <span>{clientsSummary.atRisk?.low || 0}</span>
                    </div>
                  </div>
                </div>

                <div style={{ fontSize: '13px', color: '#666' }}>
                  <p style={{ margin: '0 0 8px 0' }}>
                    <strong>CBC (Therapeutic Drift):</strong> Monitors when conversations
                    drift away from therapeutic content into casual chat.
                  </p>
                  <p style={{ margin: '0' }}>
                    <strong>Parasocial Detection:</strong> Identifies unhealthy attachment
                    patterns, dependency, and AI-related delusions.
                  </p>
                </div>

                <div style={{
                  marginTop: '20px',
                  padding: '12px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '6px',
                  fontSize: '12px',
                  color: '#666'
                }}>
                  <strong>Tip:</strong> Click on a client to view detailed boundary metrics
                  and take action on any flags.
                </div>
              </div>
            ) : (
              <div style={{ color: '#666', textAlign: 'center', padding: '20px' }}>
                {loading ? 'Loading...' : 'No data available'}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BoundaryMonitoringDashboard;
